import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Reusable accordion-based FAQ section.
 * `items` - array of { q, a }
 * `groups` - optional: array of { title, questions: [{q, a}] }
 */
export default function FAQSection({
  title = 'Najczęściej zadawane pytania',
  subtitle,
  items,
  groups,
  background = 'bg-white',
  maxWidth = 'max-w-4xl',
}) {
  const allItems = groups
    ? groups.flatMap((g) => g.questions)
    : items || [];

  return (
    <section className={`py-24 ${background}`}>
      <div className={`${maxWidth} mx-auto px-6`}>
        {title && (
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4">
              {title}
            </h2>
            {subtitle && (
              <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {groups ? (
          <div className="space-y-12">
            {groups.map((group) => (
              <div key={group.title}>
                <h3 className="text-xl font-heading font-bold text-[#2c3e50] mb-4 pb-2 border-b border-[#2c3e50]/10">
                  {group.title}
                </h3>
                <FAQList items={group.questions} />
              </div>
            ))}
          </div>
        ) : (
          <FAQList items={allItems} />
        )}
      </div>

      {/* FAQ Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: allItems.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          }),
        }}
      />
    </section>
  );
}

function FAQList({ items }) {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const isOpen = openIdx === idx;
        return (
          <div
            key={idx}
            className="bg-white rounded-lg border border-[#2c3e50]/10 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : idx)}
              className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#edf1f5]/50 transition-colors"
              aria-expanded={isOpen}
            >
              <span className="font-paragraph font-semibold text-[#2c3e50] text-base sm:text-lg">
                {item.q}
              </span>
              <ChevronDown
                size={20}
                className={`text-[#2c3e50]/50 shrink-0 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 text-[#2c3e50]/70 font-paragraph leading-relaxed">
                {item.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
